import {
  faPlus as AddIcon,
  faTrash as DeleteIcon,
  faPen as EditIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import { truncate } from 'lodash';
import { useMemo, useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import TableWidget from '@/components/TableWidget';
import PERMISSIONS from '@/constants/auth.constants';
import AiTicketTemplateDialog from '@/features/AITicketTemplateDialog';
import MessageTemplateDialog from '@/features/MessageTemplateDialog';
import RecommendationTemplateDialog from '@/features/RecommendationTemplateDialog';
import TicketTemplateDialog from '@/features/TicketTemplateDialog';
import useConfirm from '@/hooks/useConfirm';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import {
  type TemplateType,
  useAdministrationTemplates
} from '@/pages/Administration/AdministrationTemplates';
import {
  useDeleteAiTicketTemplate,
  useFetchAITicketTemplates
} from '@/pages/AlertRules/AlertRules.store';

import DEFAULT_TEMPLATE_VALUES from './AdministrationTemplates.constants';
import useStyles from './AdministrationTemplates.styles';

const AdministrationTemplates = () => {
  const [open, setOpen] = useState(false);
  const [templateType, setTemplateType] = useState<string>('');
  const {
    deleteTemplate,
    isFetchingMessageTemplates,
    isFetchingRecommendationTemplates,
    isFetchingTicketTemplates,
    messageTemplates,
    recommendationTemplates,
    ticketTemplates
  } = useAdministrationTemplates();
  const { mutate: deleteAiTicketTemplate } = useDeleteAiTicketTemplate();
  const { data: aiTicketTemplates, isFetching: isFetchingAITicketTemplates } =
    useFetchAITicketTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>(
    DEFAULT_TEMPLATE_VALUES
  );
  const hasAiPermission = usePermissionsCheck(PERMISSIONS.CASES_UPDATE);
  const { classes } = useStyles({ hasAiPermission });

  const { ConfirmationModal: TemplateDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Template?',
      message: 'Please confirm that you want to delete this template.',
      button: 'Delete'
    });

  const onDelete = async (row: TemplateType) => {
    let templateLabel: string;
    if (row.template) {
      templateLabel = 'recommendation';
    } else if (row.summary && !row.name) {
      templateLabel = 'ticket';
    } else if (row.name) {
      templateLabel = 'AI Ticket';
    } else {
      templateLabel = 'message';
    }
    const confirmed = await confirmDelete();
    if (confirmed && templateLabel === 'AI Ticket') {
      deleteAiTicketTemplate({
        id: row.id as number
      });
    } else if (confirmed) {
      deleteTemplate({
        type: templateLabel,
        id: row.id as number
      });
    }
  };

  const onEditClick = (row: TemplateType) => {
    if (row.template) {
      setTemplateType('Edit Recommendation');
    } else if (row.summary && !row.name) {
      setTemplateType('Edit Ticket');
    } else if (row.name) {
      setTemplateType('Edit AI Ticket');
    } else {
      setTemplateType('Edit Message');
    }
    setSelectedTemplate(row);
    setOpen(true);
  };

  const columns = [
    {
      field: 'template_name',
      flex: 1,
      renderCell: (params: { row: TemplateType }) => (
        <Stack
          direction="row"
          sx={{ width: '100%', justifyContent: 'space-between' }}
        >
          <Typography>
            {params.row.template_name
              ? truncate(params.row.template_name, { length: 50 })
              : truncate(params.row.template, { length: 50 })}
          </Typography>
          <Stack direction="row">
            <Button onClick={() => onEditClick(params.row)}>
              <FontAwesomeIcon icon={EditIcon} />
            </Button>
            <Button onClick={() => onDelete(params.row)}>
              <FontAwesomeIcon icon={DeleteIcon} />
            </Button>
          </Stack>
        </Stack>
      )
    }
  ];

  const aiTicketTemplateColumns = [
    {
      field: 'name',
      flex: 1,
      renderCell: (params: { row: TemplateType }) => (
        <Stack
          direction="row"
          sx={{ width: '100%', justifyContent: 'space-between' }}
        >
          <Typography>{truncate(params.row.name, { length: 50 })}</Typography>
          <Stack direction="row">
            <Button onClick={() => onEditClick(params.row)}>
              <FontAwesomeIcon icon={EditIcon} />
            </Button>
            <Button
              disabled={params.row.id === 1}
              onClick={() => onDelete(params.row)}
            >
              <FontAwesomeIcon icon={DeleteIcon} />
            </Button>
          </Stack>
        </Stack>
      )
    }
  ];

  const newTemplate = (type: string) => {
    return (
      <Stack
        sx={{ cursor: 'pointer', alignItems: 'center' }}
        direction="row"
        spacing={1}
      >
        <Button
          startIcon={<FontAwesomeIcon color="primary" icon={AddIcon} />}
          onClick={() => {
            setOpen(true);
            setTemplateType(type);
            setSelectedTemplate(DEFAULT_TEMPLATE_VALUES);
          }}
        >
          New Template
        </Button>
      </Stack>
    );
  };

  const templateDialog = useMemo(() => {
    if (templateType === 'New Ticket' || templateType === 'Edit Ticket') {
      return (
        <TicketTemplateDialog
          setOpen={setOpen}
          open={open}
          selectedTemplate={selectedTemplate}
          templateType={templateType}
        />
      );
    }
    if (templateType === 'New Message' || templateType === 'Edit Message') {
      return (
        <MessageTemplateDialog
          setOpen={setOpen}
          open={open}
          selectedTemplate={selectedTemplate}
          templateType={templateType}
        />
      );
    }
    if (
      templateType === 'New Recommendation' ||
      templateType === 'Edit Recommendation'
    ) {
      return (
        <RecommendationTemplateDialog
          setOpen={setOpen}
          open={open}
          selectedTemplate={selectedTemplate}
          templateType={templateType}
        />
      );
    }
    if (templateType === 'New AI Ticket' || templateType === 'Edit AI Ticket') {
      return (
        <AiTicketTemplateDialog
          setOpen={setOpen}
          open={open}
          selectedTemplate={selectedTemplate}
          templateType={templateType}
        />
      );
    }
    return null;
  }, [open, selectedTemplate, templateType, setOpen]);

  return (
    <LargeCard title="Templates">
      <Stack direction="row" spacing={2} sx={{ width: '100%', height: 400 }}>
        <CheckPermissions permissions={[PERMISSIONS.CASES_UPDATE]}>
          <div className={classes.templateContainer}>
            <TableWidget
              columns={aiTicketTemplateColumns}
              rows={aiTicketTemplates || []}
              title={<Typography variant="h5">AI Ticket Templates</Typography>}
              headerAction={newTemplate('New AI Ticket')}
              loading={isFetchingAITicketTemplates}
            />
          </div>
        </CheckPermissions>
        <div className={classes.templateContainer}>
          <TableWidget
            columns={columns}
            rows={ticketTemplates}
            title={<Typography variant="h5">Ticket Templates</Typography>}
            headerAction={newTemplate('New Ticket')}
            loading={isFetchingTicketTemplates}
          />
        </div>
        <div className={classes.templateContainer}>
          <TableWidget
            columns={columns}
            rows={messageTemplates}
            title={<Typography variant="h5">Message Templates</Typography>}
            headerAction={newTemplate('New Message')}
            loading={isFetchingMessageTemplates}
          />
        </div>
        <div className={classes.templateContainer}>
          <TableWidget
            columns={columns}
            rows={recommendationTemplates}
            title={
              <Typography variant="h5">Recommendation Templates</Typography>
            }
            headerAction={newTemplate('New Recommendation')}
            loading={isFetchingRecommendationTemplates}
          />
        </div>
      </Stack>
      {open && templateDialog}
      <TemplateDeleteModal />
    </LargeCard>
  );
};

export default AdministrationTemplates;
